import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';

import { displayToastSuccess } from '@/helpers';
import { useApiError } from '@/modules/common/hooks/use-api-error';
import { invalidateTeamMemberRelatedQueries } from '@/modules/common/utils/query-invalidation';

import { deleteTeamMembers } from '../api/team.api';

/**
 * Hook for bulk deleting team members.
 * Handles mutation, cache invalidation, and toast notifications.
 *
 * @param onSuccess - Optional callback to execute after successful deletion
 * @returns TanStack Query mutation for deleting team members
 */
export const useDeleteTeamMembersBulk = ({ onSuccess }: { onSuccess?: () => void }) => {
  const queryClient = useQueryClient();
  const { t } = useTranslation('authenticated.team');
  const { displayToastErrorFromApiWithFallback } = useApiError();

  return useMutation({
    mutationFn: (memberIds: number[]) => deleteTeamMembers(memberIds),
    onSuccess: async (_, memberIds) => {
      // Invalidate all team member related queries and their dependencies
      await invalidateTeamMemberRelatedQueries(queryClient);

      displayToastSuccess(
        t('toasts.teamMembersDeletedSuccess', {
          count: memberIds.length,
        })
      );

      if (onSuccess) {
        onSuccess();
      }
    },
    onError: error => {
      displayToastErrorFromApiWithFallback(error, 'toasts.deleteTeamMembersError');
    },
  });
};
